import React, { useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faMars,
  faVenus,
  faPrint,
} from "@fortawesome/free-solid-svg-icons";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";

// Fungsi untuk mengkapitalkan huruf pertama setiap kata
const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Fungsi untuk mengonversi kode CIO atau nama negara ke kode ISO untuk bendera
const getFlagCode = (nationality) => {
  if (!nationality) return "un";
  const codeMap = {
    INA: "id",
    INDONESIA: "id",
    GERMANY: "de",
    RUSSIA: "ru",
    FRANCE: "fr",
    USA: "us",
    GBR: "gb",
    MALAYSIA: "my",
    MY: "my",
    JAPAN: "jp",
    JPN: "jp",
  };
  return codeMap[nationality.toUpperCase()] || "un";
};

// Fungsi untuk mengonversi gender ke format dengan ikon
const formatGender = (gender, isMixGroup) => {
  if (isMixGroup) {
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" />
        <FontAwesomeIcon icon={faVenus} className="mr-1" />
        MIX
      </>
    );
  }
  if (!gender) return "-";
  if (gender === "M" || gender === "Laki-laki")
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" /> Male
      </>
    );
  if (gender === "F" || gender === "Perempuan")
    return (
      <>
        <FontAwesomeIcon icon={faVenus} className="mr-1" /> Female
      </>
    );
  return gender;
};

// Fungsi untuk mendapatkan nama kategori yang ditampilkan
const getCategoryDisplay = (category) => {
  return (
    {
      "7-vertical": "Vertical (7K)",
      "7-fun": "Fun Trail (7K)",
      "20-classic": "Classic Distance (20K)",
      "20-mix": "Mix Group (20K)",
      40: "Short Distance (40K)",
      80: "Long Distance (80K)",
    }[category] || "Unknown Category"
  );
};

// Fungsi untuk mendapatkan tanggal start berdasarkan kategori
const getStartDateTime = (category) => {
  const categoryStartTimes = {
    "7-vertical": "27 Sept 2025 at 06:30",
    "7-fun": "27 Sept 2025 at 07:00",
    "20-classic": "27 Sept 2025 at 03:00",
    "20-mix": "27 Sept 2025 at 03:00",
    40: "27 Sept 2025 at 05:30",
    80: "27 Sept 2025 at 01:00",
  };
  return categoryStartTimes[category] || "Unknown Date";
};

export const ResultsDetail = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  // Scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Jika tidak ada state, kembalikan ke halaman results
  if (!state || !state.name || !state.ranking) {
    navigate("/results");
    return null;
  }

  const {
    name,
    ranking,
    category,
    bibNumber,
    nationality,
    gender,
    status,
    time,
    genderRanking,
  } = state;

  const generateCertificate = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error("Canvas tidak tersedia");
      return;
    }

    const ctx = canvas.getContext("2d");
    const template = new Image();
    template.src = "/certificate.png"; // Pastikan template sertifikat tersedia di public folder

    template.onload = () => {
      canvas.width = template.width;
      canvas.height = template.height;

      ctx.drawImage(template, 0, 0);

      // Menyesuaikan kategori untuk tampilan sertifikat
      const categoryDisplay = getCategoryDisplay(category);

      // Tulis nama pada sertifikat
      ctx.font = "bold 120px 'Merriweather', serif";
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.fillText(name, canvas.width / 2, canvas.height / 2 + 20);

      // Tulis kategori
      ctx.font = "52px 'Merriweather', serif";
      ctx.textAlign = "center";
      ctx.fillText(categoryDisplay, canvas.width / 2, canvas.height / 2 - 288);

      // Tulis peringkat
      ctx.font = "50px 'Merriweather', serif";
      ctx.textAlign = "center";
      ctx.fillText(
        `Rank: ${ranking}`,
        canvas.width / 2,
        canvas.height / 2 + 148
      );

      // Unduh sertifikat
      const link = document.createElement("a");
      link.download = `${name}-Certificate.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };

    template.onerror = (e) => {
      console.error("Error loading certificate template:", e);
    };
  };

  // Mendapatkan tanggal dan waktu start
  const startDateTime = getStartDateTime(category);

  return (
    <section className="min-h-screen py-20 bg-white">
      <div className="container mx-auto px-4">
        <button
          onClick={() => navigate("/results")}
          className="mb-6 flex items-center text-lime-600 hover:text-lime-700 transition-colors font-oxanium text-sm"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Kembali ke Hasil Lomba
        </button>
        <h1 className="text-2xl font-bold text-lime-600 mb-6">
          JOTR Race Results
        </h1>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Kolom Kiri (30%) - Informasi */}
          <div className="w-full md:w-[30%] bg-white border rounded-2xl border-gray-300 shadow-sm flex flex-col items-center text-center">
            {/* Nama */}
            <h3 className="text-xl font-bold text-lime-600 uppercase tracking-wide py-4 w-full">
              {name}
            </h3>

            {/* Nomor BIB dengan latar hijau muda dan border lime */}
            <div className="bg-lime-50 text-lime-600 text-4xl font-extrabold py-3 w-full  border-y border-gray-200">
              {bibNumber || "-"}
            </div>

            {/* Kategori & tanggal */}
            <div className="py-3 border-b border-gray-200 w-full">
              <p className="text-base font-semibold text-gray-800">
                {getCategoryDisplay(category)}
              </p>
              <p className="text-sm text-gray-600">{startDateTime}</p>
            </div>

            {/* Detail info */}
            <div className="text-sm text-gray-700 py-3 space-y-1 w-full">
              <p>
                <span className="font-semibold">Gender :</span>{" "}
                {formatGender(gender, category === "20-mix")}
              </p>
              <p>
                <span className="font-semibold">Category :</span>{" "}
                {getCategoryDisplay(category)}
              </p>
            </div>
          </div>

          {/* Kolom Kanan (70%) - Tabel Detail dan Tombol Print Certificate */}
          <div className="w-full md:w-[70%] bg-white rounded-xl border border-gray-300 overflow-hidden flex flex-col">
            <table className="min-w-full divide-y divide-gray-200 table-auto mb-6">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">
                    Overall Rank
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">
                    Gender Rank
                  </th>

                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gender
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-2 py-4 text-sm text-gray-600 font-bold text-center">
                    {ranking ? `${ranking} All` : "-"}
                  </td>

                  <td className="px-2 py-4 text-sm text-gray-600 font-bold text-center">
                    {genderRanking
                      ? `${genderRanking} ${
                          gender
                            ? gender === "M"
                              ? "Male"
                              : gender === "F"
                              ? "Female"
                              : gender
                            : "-"
                        }`
                      : "-"}
                  </td>

                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    <span className="border border-gray-300 rounded-full px-2 py-1 inline-flex items-center">
                      <img
                        src={`https://flagicons.lipis.dev/flags/4x3/${getFlagCode(
                          nationality
                        )}.svg`}
                        className="country-flag w-4 h-3 mr-1 border border-gray-300 rounded"
                        alt={nationality}
                      />
                      {nationality || "-"}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    <span className="border border-gray-300 rounded-full px-2 py-1">
                      {formatGender(gender, category === "20-mix")}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-xs text-lime-600">
                    <span className="bg-lime-50 border border-lime-400 rounded-full px-2 py-1">
                      FINISHED
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="text-center px-4 pb-4">
              <button
                onClick={generateCertificate}
                className="w-full bg-lime-600 text-white py-2 px-4 rounded-xl hover:bg-lime-700 font-oxanium text-sm transition-colors flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon
                  icon={faFileLines}
                  className="text-white w-4 h-4"
                />
                Print Certificate
              </button>
            </div>
          </div>
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
    </section>
  );
};

export default ResultsDetail;
